<script setup lang="ts">
import { computed, ref } from 'vue';
import moment from 'moment';
import {
    BChip, BIcon, BMenu, BDateRangeWithPresets,
} from '@/design-system';
import { useTranslation } from '@/composables';
import type { DateRangeType } from '@/types/statistics/DateRangeType';
import type { GroupingPeriodType } from '@/types/GroupingPeriodType';

type PropsType = {
    modelValue: DateRangeType | undefined;
    presets?: GroupingPeriodType[];
};

const props = withDefaults(defineProps<PropsType>(), { presets: () => ['day', 'week', 'month'] });

const emit = defineEmits<{
    'update:modelValue': [value: DateRangeType | undefined];
}>();

const { t } = useTranslation();
const isMenuOpen = ref(false);

const formattedDateRange = computed(() => {
    if (!props.modelValue) {
        return t('common.selectDate');
    }
    const start = moment(props.modelValue.start).format(t('dateFormats.dateShort'));
    const end = moment(props.modelValue.end).format(t('dateFormats.date'));

    return `${start} – ${end}`;
});

const handleDateRangeChange = (newRange: DateRangeType | undefined) => {
    emit('update:modelValue', newRange);
    if (newRange) {
        isMenuOpen.value = false;
    }
};
</script>

<template>
    <BMenu
        v-model="isMenuOpen"
        :close-on-content-click="false"
        location="top"
    >
        <template #activator="{ props: menuProps }">
            <BChip
                v-bind="menuProps"
                variant="flat"
                class="cursor-pointer px-4"
            >
                <BIcon
                    icon="mdi-calendar-range"
                    size="small"
                    class="mr-2"
                />
                {{ formattedDateRange }}
                <BIcon
                    icon="mdi-chevron-up"
                    size="default"
                    class="ml-2"
                />
            </BChip>
        </template>

        <template #list-items>
            <div class="date-picker-menu">
                <BDateRangeWithPresets
                    :model-value="modelValue"
                    :presets="presets"
                    max-width="100%"
                    hide-label
                    @update:model-value="handleDateRangeChange"
                />
            </div>
        </template>
    </BMenu>
</template>

<style scoped lang="scss">
.cursor-pointer {
    cursor: pointer;
}

.date-picker-menu {
    background: rgb(var(--v-theme-surface));
    border-radius: 8px;
    padding: 16px;
    min-width: 320px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
</style>
